/**
 * custom-article.js
 * PHOTOPRI — Blog Interactive Elements
 * "The Gallery Spotlight" Edition
 *
 * アーティスト特化の有機的インタラクションを提供します。
 * 1. IntersectionObserver によるスタガー・フェードイン
 * 2. マウス座標を追従する「スポットライト」エフェクト（PCのみ）
 */

document.addEventListener('DOMContentLoaded', () => {
  /* =========================================================================
     1. スクロール時のフェードイン（IntersectionObserver）
     ========================================================================= */
  const offerCards = document.querySelectorAll('.st-premium-offer-card');

  if (offerCards.length > 0) {
    // 1.1 Shopifyのエディタ仕様により空のdiv要素が自動削除される問題の回避策
    // カード内の必須レイヤー（オーバーレイ、スポットライト、ノイズ）が欠損している場合は自動生成して挿入する
    offerCards.forEach(card => {
      const contentLayer = card.querySelector('.st-premium-offer-content');
      if (contentLayer) {
        const requiredLayers = [
          { className: 'st-premium-offer-overlay' },
          { className: 'st-premium-offer-spotlight' },
          { className: 'st-premium-offer-noise' }
        ];
        
        requiredLayers.forEach(layerDef => {
          const existing = card.querySelector(`.${layerDef.className}`);
          if (!existing) {
            const div = document.createElement('div');
            div.className = `${layerDef.className} ignore-empty`;
            div.setAttribute('aria-hidden', 'true');
            // コンテンツレイヤーの前に挿入して正しいZ-index順序を確保
            card.insertBefore(div, contentLayer);
          } else {
            // 既存要素にも ignore-empty を付与（テーマの div:empty { display:none } 対策）
            existing.classList.add('ignore-empty');
          }
        });
      }
    });

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          obs.unobserve(entry.target);
        }
      });
    }, {
      root: null,
      rootMargin: '0px 0px -10% 0px',
      threshold: 0
    });

    offerCards.forEach(card => observer.observe(card));
  }

  /* =========================================================================
     2. スポットライト追従（Spotlight Tracking）
     PCのみ。カード内でマウスが動くと、CSS変数を更新して光が追従する。
     ========================================================================= */
  const isPointerDevice = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  if (isPointerDevice) {
    document.querySelectorAll('[data-spotlight]').forEach(card => {
      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        card.style.setProperty('--mouse-x', x + '%');
        card.style.setProperty('--mouse-y', y + '%');
      });

      card.addEventListener('mouseleave', () => {
        // マウスが離れたらスポットライトを中央へゆっくり戻す
        card.style.setProperty('--mouse-x', '50%');
        card.style.setProperty('--mouse-y', '50%');
      });
    });
  }

  /* st-blog-carousel:begin */
  /* =========================================================================
     3. 横スワイプ・カルーセルのドット（現在位置インジケータ）と矢印
     記事本文には <div class="st-blog-carousel"><img>…</div> だけを置く。
     ドット・矢印・ラッパはここで生成するので、記事側に空要素を書く必要がない
     （空 div はエディタに削除されるため。上の st-premium-offer-card と同じ理由）。
     ========================================================================= */
  document.querySelectorAll('.st-blog-carousel').forEach(track => {
    const slides = Array.from(track.children);
    // ラッパで包むと兄弟関係が変わるため、再入防止はトラック自身の印で持つ
    if (slides.length < 2 || track.dataset.stBlogCarousel === 'ready') return;
    track.dataset.stBlogCarousel = 'ready';

    // 矢印を重ねる土台。トラックをその場で包む
    const wrap = document.createElement('div');
    wrap.className = 'st-blog-carousel-wrap';
    track.parentNode.insertBefore(wrap, track);
    wrap.appendChild(track);

    const makeArrow = (dir, label) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'st-blog-carousel-arrow st-blog-carousel-arrow--' + dir;
      btn.setAttribute('aria-label', label);
      return btn;
    };
    const prevArrow = makeArrow('prev', '前の画像へ');
    const nextArrow = makeArrow('next', '次の画像へ');
    wrap.appendChild(prevArrow);
    wrap.appendChild(nextArrow);

    const dots = document.createElement('div');
    dots.className = 'st-blog-carousel-dots';
    slides.forEach((slide, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'st-blog-carousel-dot' + (i === 0 ? ' is-active' : '');
      dot.setAttribute('aria-label', (i + 1) + '枚目を表示');
      dot.addEventListener('click', () => moveTo(i));
      dots.appendChild(dot);
    });
    wrap.insertAdjacentElement('afterend', dots);

    const buttons = Array.from(dots.children);
    let current = 0;
    let pending = null;   // 矢印/ドットで送っている最中の着地先
    let pendingTimer = 0;

    const paint = index => {
      current = index;
      buttons.forEach((b, i) => b.classList.toggle('is-active', i === index));
      prevArrow.disabled = index === 0;
      nextArrow.disabled = index === slides.length - 1;
    };

    // いま画面中央に最も近いスライドの番号
    const nearest = () => {
      const center = track.scrollLeft + track.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      slides.forEach((slide, i) => {
        const dist = Math.abs(slide.offsetLeft - track.offsetLeft + slide.offsetWidth / 2 - center);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    };

    const moveTo = index => {
      const i = Math.max(0, Math.min(slides.length - 1, index));
      const slide = slides[i];
      if (!slide) return;
      // 着地を待たずに現在位置を進める。待つと連打で同じ場所に留まる
      pending = i;
      clearTimeout(pendingTimer);
      pendingTimer = setTimeout(() => { pending = null; }, 800);
      paint(i);
      track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
    };

    prevArrow.addEventListener('click', () => moveTo(current - 1));
    nextArrow.addEventListener('click', () => moveTo(current + 1));

    let ticking = false;
    track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const at = nearest();
        // 送っている途中の中間位置で表示を巻き戻さない
        if (pending !== null) {
          if (at !== pending) return;
          pending = null;
          clearTimeout(pendingTimer);
        }
        paint(at);
      });
    }, { passive: true });

    paint(nearest());
  });
  /* st-blog-carousel:end */
});
